import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  SvgIcon,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useStopwatch } from 'src/layouts/check-in-checkout/StopwatchContext';
import { permissions } from 'src/paths';
import { Permissions, usePermission } from 'src/rbac';
import { ClockIcon, ClockInIcon } from '../dashboard-icon';
import useFeatureToggle from 'src/hooks/use-feature-toggle';
import useClockInOutButtonVisibility from 'src/hooks/use-clock-in-out-button-visibility';
import { clockInActivityType } from 'src/config';

type Props = {};
const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  borderRadius: '10px',
  transform: 'translate(-50%, -50%)',
  width: '30%',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
};

const { LABOR_MANAGEMENT_UPDATE } = permissions;

const TimeInTimeOut = (props: Props) => {
  const { formattedTime, isRunning, startStopwatch, stopStopwatch, time, globalLocationType } =
    useStopwatch();
  // APIT-587: manual Clock In/Out button visibility is driven by the per-facility flag
  // (single source of truth), replacing the old enterprise enablePaycom/enableHostedTime/
  // enablePaylocity gate. true = show the button, false/404/error = hide.
  const { showClockInOutButton } = useClockInOutButtonVisibility();
  const [checkActivityType] = useFeatureToggle(clockInActivityType, 'ENTERPRISE');
  const dispatch = useDispatch();
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );
  // Segment B: Rehab-Trav labor-code affordance migrated to the instrumented scopeKey path.
  const canApplyRehabTravCode = usePermission(Permissions.APPLY_REHAB_TRAV_LABOR_CODE);

  //get
  const getTimeCheckIn = useSelector((state: any) => state.checkInReducer);

  // post
  const checkoutResponse = useSelector((state: any) => state.checkoutReducer);

  useEffect(() => {
    localStorage.setItem('lastRunningTime', time?.toString());
    localStorage.setItem('isCheckIn', isRunning ? 'true' : 'false');
  }, [time, isRunning]);

  const [isCheck, setIsCheck] = useState(false);
  const getTherapistProfile = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data
  );
  const [isSubsequentBuilding, setIsSubsequentBuilding] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);
  const [millage, setMillage] = useState<null | number>(null);
  const [open, setOpen] = useState(false);

  const [activityType, setActivityType] = useState('');

  const handleChangeActiveType = (event: React.ChangeEvent<HTMLInputElement>) => {
    localStorage.setItem('activityType', (event.target as HTMLInputElement).value);
    setActivityType((event.target as HTMLInputElement).value);
  };

  const handleClose = () => {
    setOpen(false);
    setMillage(null);
    setIsBuilding(false);
    setIsCheck(false);
    seErrorForMillage(false);
    setErrorWhileAdd(false);
  };

  const [errorForMillage, seErrorForMillage] = useState(false);
  const [errorWhileAdd, setErrorWhileAdd] = useState(false);

  const handleSaveMillage = () => {
    if (errorForMillage) return;
    if (isCheck && !millage) {
      setErrorWhileAdd(true);
      return;
    }

    const millageInfo = {
      locationType: isCheck ? 'MILEAGE' : isBuilding ? 'DRIVE' : 'BUILDING',
      payByMilage: millage,
      isSubsequentBuilding,
      activityType,
    };
    startStopwatch('MILLAGE', millageInfo);
    setMillage(null);
    setIsBuilding(false);
    setIsCheck(false);
  };

  const handleMillageChange = (e: any) => {
    const inputValue = e.target.value.trim();

    if (inputValue || inputValue.length > 1) {
      if (/^\d+$/.test(inputValue)) {
        const newValue = parseInt(inputValue, 10);
        setMillage(newValue);
        seErrorForMillage(false);
        setErrorWhileAdd(false);
      } else {
        seErrorForMillage(true);
        setErrorWhileAdd(true);
      }
    } else {
      setMillage(null);
      seErrorForMillage(true);
      setErrorWhileAdd(true);
    }
  };

  const handleChangeSwitch = (e: any) => {
    setIsCheck(e.target.checked);
    seErrorForMillage(false);
    setErrorWhileAdd(false);
    if (!e.target.checked) {
      setMillage(null);
    }
  };

  useEffect(() => {
    if (!checkoutResponse?.isGetCheckIn && checkoutResponse?.response === null) return;
    if (!checkoutResponse.isGetCheckIn && checkoutResponse?.response !== null) {
      setOpen(false);
      return;
    }
    if (checkoutResponse.isGetCheckIn && checkoutResponse?.response !== null) {
      if (globalLocationType === 'MILEAGE') {
        return setOpen(false);
      }
      if (checkoutResponse?.clockAction === 'CLOCK_IN_SUCCESS') {
        return setOpen(false);
      }
    }
  }, [checkoutResponse]);

  const handleClockIn = (e: any) => {
    e.preventDefault();
    localStorage.removeItem('activityType');
    setActivityType('');
    setOpen(true);
  };

  return (
    <>
      {showClockInOutButton && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {' '}
          <Typography
            variant="overline"
            sx={{ color: 'rgba(17, 25, 39, 0.38)' }}
          >
            Time in:{' '}
            {checkoutResponse?.loading || getTimeCheckIn?.loading ? '__  __  __' : formattedTime}
          </Typography>
          <Stack
            direction="row"
            spacing={4}
          >
            {isRunning ? (
              <LoadingButton
                startIcon={
                  <SvgIcon>
                    <ClockIcon />
                  </SvgIcon>
                }
                variant="outlined"
                sx={{
                  whiteSpace: 'nowrap',
                  height: 40,
                  width: 150,
                  '.MuiLoadingButton-loadingIndicator': {
                    left: 32,
                  },
                }}
                loadingPosition="start"
                disabled={checkoutResponse?.loading || getTimeCheckIn?.loading}
                loading={checkoutResponse?.loading || getTimeCheckIn?.loading}
                onClick={stopStopwatch}
              >
                Clock Out
              </LoadingButton>
            ) : (
              <LoadingButton
                disabled={checkoutResponse?.loading || getTimeCheckIn?.loading}
                startIcon={
                  <SvgIcon>
                    <ClockInIcon />
                  </SvgIcon>
                }
                variant="contained"
                sx={{
                  whiteSpace: 'nowrap',
                  height: 40,
                  width: 150,
                  ':hover': {
                    background: '#6366F1',
                  },
                  '.MuiLoadingButton-loadingIndicator': {
                    left: 32,
                  },
                }}
                loadingPosition="start"
                // onClick={startStopwatch}
                onClick={(e) => handleClockIn(e)}
                // onContextMenu={(e) => handleDoubleClick(e)}
                loading={checkoutResponse?.loading || getTimeCheckIn?.loading}
              >
                Clock In
              </LoadingButton>
            )}
          </Stack>
        </div>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Stack
            justifyContent="space-between"
            alignItems={'center'}
            flexDirection="row"
            sx={{ padding: '1.5rem' }}
          >
            <Typography
              id="modal-modal-title"
              variant="h5"
            >
              Clock In Details
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ border: '1px solid #EBEBEB' }} />
          <Stack
            gap="1.5rem"
            sx={{ padding: '1.5rem' }}
          >
            <Stack gap="1rem">
              <Typography
                sx={{ color: '#6C737F' }}
                variant="body2"
              >
                Location
              </Typography>
              <Stack flexDirection="row">
                <Stack
                  sx={{
                    width: '87px',
                    height: '36px',
                    borderRadius: '12px',
                    borderTopRightRadius: '0',
                    cursor: 'pointer',
                    borderBottomRightRadius: '0',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isBuilding === false ? '#6366F1' : '#F5F5F5',
                    color: isBuilding === false ? 'white' : '#6C737F',
                  }}
                  onClick={() => {
                    setIsBuilding(false), setIsCheck(false);
                    setMillage(null);
                    setIsSubsequentBuilding(false);
                  }}
                >
                  Building
                </Stack>
                <Stack
                  sx={{
                    width: '87px',
                    height: '36px',
                    borderRadius: '12px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderTopLeftRadius: '0',
                    borderBottomLeftRadius: '0',
                    cursor: 'pointer',
                    backgroundColor: isBuilding ? '#6366F1' : '#F5F5F5',
                    color: isBuilding ? 'white' : '#6C737F',
                  }}
                  onClick={() => {
                    setIsBuilding(true);
                    setIsSubsequentBuilding(false);
                    setActivityType('');
                  }}
                >
                  Drive
                </Stack>
              </Stack>
            </Stack>
            {isBuilding ? (
              <Stack
                alignItems="center"
                flexDirection="row"
                gap=".5rem"
              >
                <Switch
                  checked={isCheck}
                  onChange={handleChangeSwitch}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
                <Typography>Pay by Milage</Typography>
              </Stack>
            ) : null}
            {!isBuilding && canApplyRehabTravCode ? (
              <Stack
                alignItems="center"
                flexDirection="row"
                gap=".5rem"
              >
                <Switch
                  checked={isSubsequentBuilding}
                  onChange={(e) => {
                    setIsSubsequentBuilding(e.target.checked);
                  }}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
                <Typography>Subsequent Building</Typography>
              </Stack>
            ) : null}
            {isCheck && (
              <TextField
                onChange={handleMillageChange}
                label="Add Milage"
                value={millage}
                error={errorForMillage || errorWhileAdd}
                helperText={(errorForMillage || errorWhileAdd) && 'Please enter a valid number'}
              />
            )}

            {permissionData?.includes(LABOR_MANAGEMENT_UPDATE) && checkActivityType ? (
              <>
                {!isBuilding && (
                  <FormControl>
                    <FormLabel id="demo-row-radio-buttons-group-label">Activity Type</FormLabel>
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                      value={activityType}
                      onChange={handleChangeActiveType}
                    >
                      <FormControlLabel
                        value="THERAPY"
                        control={<Radio />}
                        label="Therapy"
                      />
                      <FormControlLabel
                        value="ADMINISTRATIVE"
                        control={<Radio />}
                        label="Administrative"
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              </>
            ) : null}

            <Stack
              flexDirection={'row'}
              justifyContent="flex-end"
              sx={{ marginTop: '1rem' }}
            >
              <Button
                color="inherit"
                onClick={handleClose}
                sx={{ maxWidth: 'max-content' }}
              >
                Cancel
              </Button>
              <Button
                sx={{ height: '36px', width: '65px' }}
                color="primary"
                variant="contained"
                onClick={handleSaveMillage}
                disabled={checkoutResponse?.loading}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default TimeInTimeOut;
